import React, { useContext, useEffect, useState, useRef } from "react";
import {
  TextField,
  Box,
  Button,
  Grid,
  InputAdornment,
  Typography,
  CircularProgress,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "components/structure/Layout";
import WoodSupplierTable from "./WoodSupplierTable";
import { SnackbarContext } from "context/snackBarContext";
import CustomPagination from "../Import/Common/CustomPagination";
import WoodSupplierTaskService from "services/WoodSupplierTaskService";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CheckboxTick from "assets/CheckboxTick.svg";
import CheckboxUntick from "assets/CheckboxUntick.svg";
import ReopenModal from "./ReopenModal";
import ApproveModal from "./ApproveModal";
import ApproveAssignModal from "./ApproveAssignModal";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "./WoodSupplierTable.css";
import UserSelectionModal from "./UserSelectionModal";
import GeneratePermanentIDDialog from "./GeneratePermanentIDDialog";
import ConfirmationDialog from "./ConfirmationDialog";
import ViewSurvey from "./ViewSurvey";
import SignaturePage from "./SignaturePage";
import { equalsIgnoreCase, hasSelectedOption } from "utils/utils";

const commonDateStyle = {
  fontFamily: "Montserrat",
  fontSize: "16px",
  lineHeight: "150%",
  letterSpacing: "0%",
  fontWeight: 500,
  fontStyle: "Medium",
};

const commonStyle = {
  fontFamily: "Montserrat",
  fontSize: "14.12px",
  lineHeight: "150%",
  letterSpacing: "0%",
  fontWeight: 600,
  fontStyle: "SemiBold",
};

dayjs.extend(utc);

const WoodSupplierTask = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [wsData, setWsData] = useState([]);
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const [cropYears, setCropYears] = useState([]);
  const [selectedCropYear, setSelectedCropYear] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(5);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [wsSurveyStatuses, setWsSurveyStatuses] = useState([]);
  const [statuses, setStatuses] = useState(["Active", "Inactive"]);
  const [assignedWoodSuppliers, setAssignedWoodSuppliers] = useState([
    "Assigned Wood Supplier",
    "Unassigned Wood Supplier",
  ]);
  const [selectedSurveyStatuses, setSelectedSurveyStatuses] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(["Active"]);
  const [selectedWoodSuppliers, setSelectedWoodSuppliers] = useState([
    "Assigned Wood Supplier",
    "Unassigned Wood Supplier",
  ]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const user = JSON.parse(sessionStorage.getItem("BAT_USER"));
  const [cropStages, setCropStages] = useState([]);
  const [selectedCropStage, setSelectedCropStage] = useState("");
  const [selectedForReopen, setSelectedForReopen] = useState(new Set());
  const [selectedForApprove, setSelectedForApprove] = useState(new Set());
  const [permanentIDModalOpen, setPermanentIDModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveAssignModalOpen, setApproveAssignModalOpen] = useState(false);
  const [isApproveModal, setIsApproveModal] = useState(false);
  const [cropStageDropdownWidth, setCropStageDropdownWidth] = useState(null);
  const cropStageRef = useRef(null);
  const [userSelectionModalOpen, setUserSelectionModalOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add a new state to store the selected crop year UID
  const [selectedCropYearUid, setSelectedCropYearUid] = useState("");

  // Add this new state for the confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [generatePermanentIDDialogOpen, setGeneratePermanentIDDialogOpen] = useState(false);
  const userProfiles = user?.profiles || [];
  const isWoodMonitor = userProfiles.some(profile =>
    profile.profileName?.toLowerCase().includes("wood monitor")
  );
  const [currentView, setCurrentView] = useState("main"); // 'main', 'view', 'signature'
  const [selectedWoodSupplier, setSelectedWoodSupplier] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  useEffect(() => {
    getCropYear();
    getWssStatusPickList(); // Add this call to fetch survey statuses

    if (cropStageRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setCropStageDropdownWidth(entry.target.offsetWidth);
        }
      });
      resizeObserver.observe(cropStageRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Add new useEffect to fetch crop seasons when crop year changes
  useEffect(() => {
    if (selectedCropYear) {
      fetchCropSeasons(selectedCropYear);
    }
  }, [selectedCropYear]);

  useEffect(() => {
    // Only fetch data if both crop year and crop stage are selected
    if (selectedCropYearUid && selectedCropStage) {
      fetchWoodSupplierTasks(currentPage, itemsPerPage);
    }
  }, [
    fromDate,
    toDate,
    selectedCropStage,
    selectedStatuses,
    selectedSurveyStatuses,
    selectedWoodSuppliers,
  ]);


  const handleCropYearChange = (e) => {
    const newCropYearName = e.target.value;
    setSelectedCropYear(newCropYearName);

    // Find the corresponding cropYearUid for the selected year name
    const selectedYearData = cropYears.find(
      (year) => year.cropYearName === newCropYearName
    );
    if (selectedYearData?.cropYearUid) {
      setSelectedCropYearUid(selectedYearData.cropYearUid);
    } else {
      setSelectedCropYearUid("");
    }

    clearSelections();
    setCurrentPage(0);
  };

  const handleCropStageChange = (e) => {
    const selectedSeason = cropStages.find(
      (season) => season.cropSeasonId === e.target.value
    );
    setSelectedCropStage(e.target.value);
    clearSelections();
    setCurrentPage(0);
  };

  const getWssStatusPickList = () => {
    WoodSupplierTaskService.fetchPickListSurvey({ pickListCodes: ["WSSSTAT"] }) // Use the WSSSTAT code for wood supplier survey statuses
      .then((res) => {
        const data = res?.data?.response?.WSSSTAT || []; // Extract the statuses from the response

        if (data.length > 0) {
          // Extract the status values from the picklist items
          const statusValues = data.map(
            (item) => item.pickListDescription || item.pickListItemCode || item
          );

          setWsSurveyStatuses(statusValues);
          setSelectedSurveyStatuses(statusValues); // Select all by default
        }
      })
      .catch((ex) => {
        console.error("Error fetching survey statuses:", ex);
        // Fallback to default values if API fails
        setWsSurveyStatuses([]);
        setSelectedSurveyStatuses([]);
      });
  };

  // Update the getCropYear function to correctly select the current crop year
  const getCropYear = () => {
    WoodSupplierTaskService?.getCropYear()
      .then((res) => {
        const cropYearData = res?.data?.response || [];
        setCropYears(cropYearData);
        // Find the current crop year (where isCurrentCropYear is true)
        const currentYear = cropYearData.find(year => year.isCurrentCropYear === true);

        if (currentYear) {
          // Set the current crop year as default
          setSelectedCropYear(currentYear.cropYearName);
          setSelectedCropYearUid(currentYear.cropYearUid);
        } else if (cropYearData.length > 0) {
          // If no current crop year is marked, select the first one
          setSelectedCropYear(cropYearData[0].cropYearName);
          setSelectedCropYearUid(cropYearData[0].cropYearUid);
        }
      })
      .catch((error) => {
        setCropYears([]);
        console.error("Error fetching crop years:", error);
      });
  };

  // Update the fetchCropSeasons function to select the first season by default
  const fetchCropSeasons = (cropYearName) => {
    // Find the cropYearUid for the selected crop year name
    const selectedYearData = cropYears.find(
      (year) => year.cropYearName === cropYearName
    );

    if (selectedYearData?.cropYearUid) {
      WoodSupplierTaskService.getCropSeasonDropDown(selectedYearData.cropYearUid)
        .then((res) => {
          // Update this section to match the new response format
          const seasons = res?.data?.response || [];
          setCropStages(seasons);
          // Always select the first crop season by default if available
          if (seasons.length > 0) {
            setSelectedCropStage(seasons[0].cropSeasonId);
          } else {
            setSelectedCropStage("");
          }
        })
        .catch((error) => {
          setCropStages([]);
        setWsData([]);
          setSelectedCropStage("");
          setLoading(false);
        });
    } else {
      setCropStages([]);
      setSelectedCropStage("");
    }
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    setCurrentPage(0);
  };

  const handleEndDateChange = (newValue) => {
    setToDate(newValue);
    setCurrentPage(0);
  };

  const fetchWoodSupplierTasks = async (pageNumber, pageSize) => {
    // Find the selected crop season to get its code
    const selectedSeasonData = cropStages.find(season => season.cropSeasonId === selectedCropStage);
    const seasonCode = selectedSeasonData?.cropSeasonCode || "";
    if (!selectedCropYearUid || !selectedCropStage) {
      return;
    }
    const params = {
      pageNumber: pageNumber,
      size: pageSize,
      cropYearId: selectedCropYearUid,
      seasonCode: seasonCode,
      ...(fromDate && {
        ftSurveyFromDate: fromDate.format("YYYY-MM-DD"),
      }),
      ...(toDate && {
        ftSurveyToDate: toDate.format("YYYY-MM-DD")
      }),
    };

    // Modified logic for assigned wood suppliers
    if (selectedWoodSuppliers?.length > 0) {
      if (selectedWoodSuppliers.length === 2) {
        // Both options selected
        params.isAssigned = "All";
      } else if (hasSelectedOption(selectedWoodSuppliers, "Assigned Wood Supplier")) {
        // Only Assigned selected
        params.isAssigned = "Assign";
      } else if (hasSelectedOption(selectedWoodSuppliers, "Unassigned Wood Supplier")) {
        // Only Unassigned selected
        params.isAssigned = "Unassign";
      }
    }
    // If no options selected, isAssigned parameter won't be included in the API call

    // Modified logic for assigned wood suppliers
    if (selectedWoodSuppliers?.length > 0) {
      if (selectedWoodSuppliers.length === 2) {
        // Both options selected
        params.isAssigned = "All";
      } else if (hasSelectedOption(selectedWoodSuppliers, "Assigned Wood Supplier")) {
        // Only Assigned selected
        params.isAssigned = "Assign";
      } else if (hasSelectedOption(selectedWoodSuppliers, "Unassigned Wood Supplier")) {
        // Only Unassigned selected
        params.isAssigned = "Unassign";
      }
    }
    // If no options selected, isAssigned parameter won't be included in the API call

    // Add wsSurveyStatus if selected
    if (selectedSurveyStatuses?.length > 0) {
      params.wsSurveyStatuses = selectedSurveyStatuses;
    }

    // Add status if selected
    if (selectedStatuses?.length > 0) {
      params.woodSupplierStatuses = selectedStatuses;
    }

    
    
    if (searchKeyword.trim() !== "") {
      params.keyword = searchKeyword;
    }
    setLoading(true);
    try {
      // Call the API
      const response = await WoodSupplierTaskService.getWoodSuppliers(params);

      // Check if response has the woodSupplierTask property and it's an array
      if (response?.data?.response?.woodSupplierTask && Array.isArray(response.data.response.woodSupplierTask)) {
        // Map the API response to the format expected by the component
        const apiData = response.data.response.woodSupplierTask;

        const formattedData = apiData.map(item => ({
          // Map API fields to the fields expected by the UI
          headerId: item.woodSupplierId,
          woodSupplierId: item.woodSupplierId,
          woodSupplierAssignmentId: item.woodSupplierAssignmentId,
          woodSupplierName: item.woodSupplierName,
          wsPermanentCode: item.wsPermanentCode,
          wsTemporaryCode: item.wsTemporaryCode,
          status: item.woodSupplierStatus,
          wsSurveyStatus: item.wsSurveyStatus,
          location: item.woodSupplierState, // Using state as location
          assignmentStatus: item.isAssigned ? "Assigned Wood Supplier" : "Unassigned Wood Supplier",
          assignedTo: item.assignedTo,
          assignedToBatOneId: item.assignedToBatOneId,
          ftSurveyDate: item.ftSurveyDate ? new Date(item.ftSurveyDate) : null,
          wsSurveyDate: item.wsSurveyDate ? new Date(item.wsSurveyDate) : null,
          createdDate: item.ftSurveyDate, // Using ftSurveyDate as createdDate
          lastModifiedDate: item.wsSurveyDate, // Using wsSurveyDate as lastModifiedDate
          wsHeaderId: item?.wsHeaderId,

          // Add UI state properties
          checked: item.isAssigned, // Set checked based on isAssigned value
          isReopen: selectedForReopen.has(item.woodSupplierId),
          isApproved: selectedForApprove.has(item.woodSupplierId)
        }));
        setWsData(formattedData);
        setTotalItems(response.data.response.totalItems || 0);
        setCurrentPage(response.data.response.currentPage || 0);
      } else {
        // If data structure is not as expected, set empty array
        setWsData([]);
        setTotalItems(0);

        // Optionally show a warning
        setSeverity("error");
        setMessage("No records found");
        setOpenAlert(true);
      }
    } catch (error) {
      console.error("Error fetching wood suppliers:", error);
      setSeverity("error");
      setMessage("No records found");
      setOpenAlert(true);
      setWsData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyStatusChange = (event) => {
    const selectedValue = event.target.value;

    if (hasSelectedOption(selectedValue, "all")) {
      setSelectedSurveyStatuses(
        selectedSurveyStatuses.length === wsSurveyStatuses.length
          ? []
          : [...wsSurveyStatuses]
      );
    } else {
      setSelectedSurveyStatuses(selectedValue);
    }
    clearSelections();
    setCurrentPage(0);
  };

  const handleStatusChange = (event) => {
    const selectedValue = event.target.value;

    if (hasSelectedOption(selectedValue, "all")) {
      setSelectedStatuses(
        selectedStatuses.length === statuses.length ? [] : [...statuses]
      );
    } else {
      setSelectedStatuses(selectedValue);
    }
    clearSelections();
    setCurrentPage(0);
  };

  const handleWoodSuppliersChange = (event) => {
    const selectedValue = event.target.value;

    if (hasSelectedOption(selectedValue, "all")) {
      setSelectedWoodSuppliers(
        selectedWoodSuppliers.length === assignedWoodSuppliers.length
          ? []
          : [...assignedWoodSuppliers]
      );
    } else {
      setSelectedWoodSuppliers(selectedValue);
    }
    clearSelections();
    setCurrentPage(0);
  };

  const handleAssignmentUpdate = (updatedItem, type) => {
    const isCurrentlyReopen = Boolean(
      wsData.find(
        (item) => item.headerId === updatedItem.headerId && item.isReopen
      )
    );
    const isCurrentlyApprove = Boolean(
      wsData.find(
        (item) => item.headerId === updatedItem.headerId && item.isApproved
      )
    );

    // Toggle the selection based on which radio was clicked and current state
    const newItem = {
      ...updatedItem,
      isReopen: updatedItem.isReopen
        ? type == "bulk"
          ? updatedItem?.isReopen
          : !isCurrentlyReopen
        : false,
      isApproved: updatedItem.isApproved
        ? type == "bulk"
          ? updatedItem.isApproved
          : !isCurrentlyApprove
        : false,
    };

    setWsData((prevData) =>
      prevData.map((item) =>
        item.headerId === newItem.headerId ? newItem : item
      )
    );

    if (newItem.isReopen) {
      setSelectedForReopen((prev) => {
        const newSet = new Set(prev);
        newSet.add(newItem.headerId);
        return newSet;
      });
      setSelectedForApprove((prev) => {
        const newSet = new Set(prev);
        newSet.delete(newItem.headerId);
        return newSet;
      });
    } else if (newItem.isApproved) {
      setSelectedForApprove((prev) => {
        const newSet = new Set(prev);
        newSet.add(newItem.headerId);
        return newSet;
      });
      setSelectedForReopen((prev) => {
        const newSet = new Set(prev);
        newSet.delete(newItem.headerId);
        return newSet;
      });
    } else {
      // If neither is selected, remove from both sets
      setSelectedForReopen((prev) => {
        const newSet = new Set(prev);
        newSet.delete(newItem.headerId);
        return newSet;
      });
      setSelectedForApprove((prev) => {
        const newSet = new Set(prev);
        newSet.delete(newItem.headerId);
        return newSet;
      });
    }
  };


  const handleAssign = () => {
    // Get all checked wood suppliers
    const checkedSuppliers = wsData.filter((ele) => ele?.checked === true && ele?.assignedToBatOneId == null);

    if (checkedSuppliers.length === 0) {
      // No wood suppliers selected, show error message
      setSeverity("error");
      setMessage("Please select at least one wood supplier to assign");
      setOpenAlert(true);
      return;
    }

    // Check if any selected suppliers don't have a permanent code
    const suppliersWithoutPermanentCode = checkedSuppliers.filter(
      (ele) => !ele.wsPermanentCode
    );

    if (suppliersWithoutPermanentCode.length > 0) {
      // Some selected suppliers don't have permanent codes
      setIsApproveModal(true);
      setApproveAssignModalOpen(true);
    } else {
      // All selected suppliers have permanent codes
      setIsApproveModal(false);

      // Check if the logged in user has the "test local user for wood supplier" role in their profiles



      if (isWoodMonitor) {
        // Show confirmation dialog for Wood Monitor users
        handleConfirmationResponse(true); // Directly call the confirmation handler with "true" to proceed
      } else {
        // For othe
        // r users, proceed normally
        setConfirmDialogOpen(true);

      }
    }
  };
  const handleConfirmationResponse2 = async (confirmed) => {
    setConfirmDialogOpen(false);
    setUserSelectionModalOpen(true);
  }

  // Add a handleRefresh function
  const handleRefresh = () => {
    // Clear all selection states
    setSelectedForReopen(new Set([]));
    setSelectedForApprove(new Set([]));

    // Reset current page to 0
    setCurrentPage(0);

    // Fetch fresh data from API
    fetchWoodSupplierTasks(0, itemsPerPage);
  };

  // Update the handleConfirmationResponse function
  const handleConfirmationResponse = async (confirmed) => {

    if (confirmed) {
      // User clicked "Yes"
      // If the current user is a Wood Monitor, directly call the assign API
      const userProfiles = user?.profiles || [];

      if (isWoodMonitor) {
        // Direct assignment for Wood Monitors
        try {
          setLoading(true);

          // Prepare the wood suppliers details - only include items that have changed status
          const woodSuppliersDetails = [];

          // 1. Items that were NOT assigned before but are now checked (to assign them)
          const newlyCheckedSuppliers = wsData.filter(
            item => item.checked && !item.assignedTo
          );

          // 2. Items that WERE assigned before but are now unchecked (to unassign them)
          const newlyUncheckedSuppliers = wsData.filter(
            item => !item.checked && item.assignedTo &&
              // Wood Monitor can only unassign their own assignments
              item.assignedToBatOneId === user.batOneId
          );

          // Add newly checked items with isAssigned: true
          newlyCheckedSuppliers.forEach(item => {
            woodSuppliersDetails.push({
              woodSupplierId: item.woodSupplierId || item.headerId,
              isAssigned: true,
            });
          });

          // Add newly unchecked items with isAssigned: false
          newlyUncheckedSuppliers.forEach(item => {
            woodSuppliersDetails.push({
              woodSupplierId: item.woodSupplierId || item.headerId,
              isAssigned: false,
            });
          });

          // Only proceed if there are changes to process
          if (woodSuppliersDetails.length === 0) {
            setSeverity("error");
            setMessage("No changes detected in wood supplier assignments");
            setOpenAlert(true);
            setLoading(false);
            return;
          }

          // Create the payload with the current user's batOneId
          const payload = {
            cropYearId: selectedCropYearUid,
            cropSeasonId: selectedCropStage,
            assignedToUserBatOneId: user.batOneId, // Use the current user's batOneId
            woodSuppliersDetails: woodSuppliersDetails,
          };
          // Call the API directly
          const response = await WoodSupplierTaskService.assignWoodSuppliers(payload);

          // Handle success
          setSeverity("success");
          setMessage(`Assigned Successfully`);
          setOpenAlert(true);

          // Refresh the data
        } catch (error) {
          // Handle error
          setSeverity("error");
          setMessage(error?.response?.data?.message || "Failed to assign wood suppliers");
          setOpenAlert(true);
        } finally {
          setLoading(false);
        }
      } else {
        // For other users, proceed by opening the user selection modal
        setUserSelectionModalOpen(true);
      }
    }
    // If user clicked "No", do nothing (dialog just closes)
  };

  const handleApprove = () => {
    // Get all checked wood suppliers
    const checkedSuppliers = wsData.filter((ele) => ele?.checked === true && ele?.assignedToBatOneId == null);

    if (checkedSuppliers.length === 0) {
      // No wood suppliers selected, show error message
      setSeverity("warning");
      setMessage("Please select at least one wood supplier to approve");
      setOpenAlert(true);
      return;
    }

    // Check which selected suppliers don't have a permanent code
    const suppliersWithoutPermanentCode = checkedSuppliers.filter(
      (ele) => !ele.wsPermanentCode
    );

    if (suppliersWithoutPermanentCode.length === 0) {
      // All selected suppliers already have permanent codes
      setSeverity("error");
      setMessage("Selected Wood Suppliers already have permanent IDs. No action required.");
      setOpenAlert(true);
      return;
    }

    // Some selected suppliers don't have permanent codes, show new confirmation dialog
    setGeneratePermanentIDDialogOpen(true);
  };

  const handleApproveConfirm = async (comment) => {
    try {
      setLoading(true);

      // Get all checked wood suppliers
      const checkedSuppliers = wsData.filter((ele) => ele?.checked === true);

      // Filter to get only those without permanent IDs
      const suppliersToApprove = checkedSuppliers.filter(
        (ele) => !ele.wsPermanentCode
      );

      if (suppliersToApprove.length === 0) {
        setSeverity("info");
        setMessage("No wood suppliers without permanent IDs found.");
        setOpenAlert(true);
        return;
      }

      // Prepare the payload
      const woodSupplierIds = suppliersToApprove.map(item =>
        item.woodSupplierId || item.headerId
      );

      const payload = {
        cropYearId: selectedCropYearUid,
        cropSeasonId: selectedCropStage,
        woodSupplierIds: woodSupplierIds,
        // Add any other required fields for the approve API
      };

      // Call the API to generate permanent IDs
      const response = await WoodSupplierTaskService.approvePermanentIds(payload);

      // Show success message
      setSeverity("success");
      setMessage(`Approved Successfully`);
      setOpenAlert(true);
      // Refresh the data to show new permanent IDs
      fetchWoodSupplierTasks(currentPage, itemsPerPage);

    } catch (error) {
      // Handle error
      setSeverity("error");
      setMessage("Failed to generate permanent IDs");
      setOpenAlert(true);
      console.error("Error generating permanent IDs:", error);
    } finally {
      setLoading(false);
      setApproveModalOpen(false);
    }
  };

  const paginate = (pageNumber, pageSize) => {
    setSelectedForReopen(new Set([]));
    setSelectedForApprove(new Set([]));
    fetchWoodSupplierTasks(pageNumber - 1, pageSize);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(0); // Reset page
      fetchWoodSupplierTasks(0, itemsPerPage); // Use page 0 instead of currentPage
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchKeyword]);

  const clearSelections = () => {
    // setSelectedForReopen(new Set());
    // setSelectedForApprove(new Set());
  };

  

  // Update the ApproveAssignModal onConfirm handler
  const handleInitialConfirm = () => {
    setApproveAssignModalOpen(false);

    // Only open user selection modal when it's an assign action
    if (!isApproveModal) {
      setUserSelectionModalOpen(true);
    } else {
      handleApproveConfirm();
    }
  };

  // Add a new handler for the final assignment
  const handleAssignConfirm = async (userId) => {
    try {
      setLoading(true);

      // Prepare the wood suppliers details - only include items that have changed status
      const woodSuppliersDetails = [];

      // 1. Items that were NOT assigned before but are now checked (to assign them)
      const newlyCheckedSuppliers = wsData.filter(
        item => item.checked && !item.assignedTo
      );

      // 2. Items that WERE assigned before but are now unchecked (to unassign them)
      const newlyUncheckedSuppliers = wsData.filter(
        item => !item.checked && item.assignedTo
      );

      // Add newly checked items with isAssigned: true
      newlyCheckedSuppliers.forEach(item => {
        woodSuppliersDetails.push({
          woodSupplierId: item.woodSupplierId || item.headerId,
          isAssigned: true,
        });
      });

      // Add newly unchecked items with isAssigned: false
      newlyUncheckedSuppliers.forEach(item => {
        woodSuppliersDetails.push({
          woodSupplierId: item.woodSupplierId || item.headerId,
          isAssigned: false,
        });
      });

      // Only proceed if there are changes to process
      if (woodSuppliersDetails.length === 0) {
        setSeverity("error");
        setMessage("No changes detected in wood supplier assignments");
        setOpenAlert(true);
        setLoading(false);
        setUserSelectionModalOpen(false);
        return;
      }

      // Create the payload with the correct cropYearId and cropSeasonId
      const payload = {
        cropYearId: selectedCropYearUid,
        cropSeasonId: selectedCropStage,
        assignedToUserBatOneId: userId,
        woodSuppliersDetails: woodSuppliersDetails,
      };


      // Call the API
      const response = await WoodSupplierTaskService.assignWoodSuppliers(payload);

      // Handle success
      setSeverity("success");
      setMessage("Assigned Successfully");
      setOpenAlert(true);

      // Clear selections without refreshing data
    } catch (error) {
      // Handle error
      setSeverity("error");
      setMessage(error?.response?.data?.message || "Failed to update wood supplier assignments");
      setOpenAlert(true);
      console.error("Error updating wood supplier assignments:", error);
    } finally {
      setLoading(false);
      setUserSelectionModalOpen(false);
    }
  };

  // Confirmation Dialog Component

  // Add this new function to handle direct unassignment
  const handleDirectUnassign = async (payload) => {
    try {
      // Call the API
      const response = await WoodSupplierTaskService.assignWoodSuppliers(payload);

      // Show success message
      setSeverity("success");
      setMessage("Unassigned Successfully");
      setOpenAlert(true);

      return true; // Return true to indicate success
    } catch (error) {
      // Handle error
      setSeverity("error");
      setMessage("Failed to unassign wood supplier");
      setOpenAlert(true);
      console.error("Error unassigning wood supplier:", error);
      return false; // Return false to indicate failure
    }
  };

  const handleViewSurvey = (woodSupplierData) => {
    setSelectedWoodSupplier(woodSupplierData);
    setCurrentView("view");
  };

  const handleShowSignature = (signatureData) => {
    setCurrentView("signature");
    console.log("signatureData2", signatureData);
    setSignatureData(signatureData);
  };

  const handleBack = () => {
    setCurrentView("main");
    setSelectedWoodSupplier(null);
  };

  return (
    <Layout title={t("woodSupplierTask.title")}>
      {currentView === "main" && (
        // Your existing main view content
        <Box className="wst-container" sx={{ position: "relative", minHeight: "100vh" }}>
          <Typography
            sx={{
              mt: "-10px",
              color: "#102A62", // Primary Blue color
              fontFamily: "Montserrat",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "30px",
              letterSpacing: 0,
              marginBottom: 1.5, // Optional: adds some spacing below the title
            }}
          >
            {t("woodSupplierTask.title")}
          </Typography>

          <Grid
            container
            columnSpacing={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "100%",
              marginTop: 1,
              marginLeft: 0.1,
              paddingTop: 3,
              paddingRight: 3,
              paddingBottom: 3,
              backgroundColor: "#EFEFEF",
              borderRadius: "5px",
            }}
          >
            <Grid item xs={12} sm={12}>
              <Typography
                sx={{
                  ...commonStyle,
                  color: "#00000099",
                  mt: "-18px",
                  mb: "7px",
                }}
              >
                {t("woodSupplierTask.lastDateofWS")}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={3.5}
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fromDate}
                  onChange={handleFromDateChange}
                  label="From Date"
                  format="DD-MM-YYYY"
                  maxDate={toDate}
                  sx={{
                    width: "48%",
                    "& .MuiOutlinedInput-root": {
                      height: "46px",
                      borderRadius: "5px",
                      cursor: "pointer",
                      ...commonDateStyle,
                      "& input": {
                        cursor: "pointer",
                        pointerEvents: "none", // Prevent typing
                      },
                      "& fieldset": {
                        borderColor: "#C4C4C4",
                        borderRadius: "5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#C4C4C4",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4C4C4",
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onClick: (e) =>
                        e.currentTarget.querySelector("button").click(),
                      InputLabelProps: {
                        sx: {
                          color: "#131619",
                          fontFamily: "Montserrat",
                          fontWeight: 500,
                          fontSize: "18px",
                          lineHeight: "20px",
                          letterSpacing: "0%",
                        },
                      },
                      inputProps: {
                        placeholder: "",
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={toDate}
                  onChange={handleEndDateChange}
                  label="To Date"
                  minDate={fromDate}
                  format="DD-MM-YYYY"
                  sx={{
                    width: "48%",
                    "& .MuiOutlinedInput-root": {
                      height: "46px",
                      borderRadius: "5px",
                      cursor: "pointer",
                      ...commonDateStyle,
                      "& input": {
                        cursor: "pointer",
                        pointerEvents: "none", // This prevents the input from capturing clicks
                      },
                      "& fieldset": {
                        borderColor: "#C4C4C4",
                        borderRadius: "5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#C4C4C4",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4C4C4",
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onClick: (e) =>
                        e.currentTarget.querySelector("button").click(), // This triggers the calendar
                      InputLabelProps: {
                        sx: {
                          color: "#131619",
                          fontFamily: "Montserrat",
                          fontWeight: 500,
                          fontSize: "18px",
                          lineHeight: "20px",
                          letterSpacing: "0%",
                        },
                      },
                      inputProps: {
                        placeholder: "",
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={1.6}>
              <TextField
                fullWidth
                select
                label={t("woodSupplierTask.cropYear")}
                variant="outlined"
                value={selectedCropYear || ""}
                onChange={handleCropYearChange}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                  "& .MuiFormLabel-root": {
                    background:
                      "linear-gradient(to bottom, #EFEFEF 60%, white 40%)",
                  },
                }}
              >
                {cropYears.map((yearData) => (
                  <MenuItem key={yearData.cropYearUid} value={yearData.cropYearName}>
                    {yearData.cropYearName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={1.6}>
              <TextField
                ref={cropStageRef}
                fullWidth
                select
                label={t("woodSupplierTask.cropSeason")}
                variant="outlined"
                value={selectedCropStage}
                onChange={handleCropStageChange}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                  "& .MuiFormLabel-root": {
                    background:
                      "linear-gradient(to bottom, #EFEFEF 60%, white 40%)",
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        width: cropStageDropdownWidth || "auto",
                        maxHeight: 273,
                      },
                    },
                  },
                }}
              >
                {cropStages.map((season) => (
                  <MenuItem
                    key={season.cropSeasonId}
                    value={season.cropSeasonId}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      display: "block",
                      fontFamily: "Montserrat",
                    }}
                  >
                    {/* Display both name and code */}
                    {season.cropSeasonName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={1.8}>
              <TextField
                fullWidth
                select
                label={t("woodSupplierTask.woodSupplierSurveyStatus")}
                variant="outlined"
                value={selectedSurveyStatuses || []} // Ensure it's always an array
                onChange={handleSurveyStatusChange}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    selected
                    // .map((status) => status.pickListDescription)
                    .join(", "),
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                  "& .MuiFormLabel-root": {
                    background:
                      "linear-gradient(to bottom, #EFEFEF 60%, white 40%)",
                  },
                }}
              >
                {wsSurveyStatuses?.map((status, index) => (
                  <MenuItem key={index} value={status}>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedSurveyStatuses.some(
                          (item) => equalsIgnoreCase(item, status)
                        )}
                        icon={<img src={CheckboxUntick} alt="Unchecked" />}
                        checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                      />
                    </ListItemIcon>
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
                <MenuItem value="all">
                  <ListItemIcon>
                    <Checkbox
                      checked={
                        selectedSurveyStatuses?.length ===
                        wsSurveyStatuses?.length
                      }
                      indeterminate={
                        selectedSurveyStatuses?.length > 0 &&
                        selectedSurveyStatuses?.length < wsSurveyStatuses?.length
                      }
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("woodSupplierTask.all")} />
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={1.6}>
              <TextField
                fullWidth
                select
                label={t("woodSupplierTask.status")}
                variant="outlined"
                value={selectedStatuses || []} // Ensure it's always an array
                onChange={handleStatusChange}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => selected.join(", "),
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                  "& .MuiFormLabel-root": {
                    background:
                      "linear-gradient(to bottom, #EFEFEF 60%, white 40%)",
                  },
                }}
              >
                {statuses?.map((status, index) => (
                  <MenuItem key={index} value={status}>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedStatuses.some((item) => equalsIgnoreCase(item, status))}
                        icon={<img src={CheckboxUntick} alt="Unchecked" />}
                        checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                      />
                    </ListItemIcon>
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
                <MenuItem value="all">
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedStatuses?.length === statuses?.length}
                      indeterminate={
                        selectedStatuses?.length > 0 &&
                        selectedStatuses?.length < statuses?.length
                      }
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("woodSupplierTask.all")} />
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={1.9}>
              <TextField
                fullWidth
                select
                label={t("woodSupplierTask.assignedWoodSuppliers")}
                variant="outlined"
                value={selectedWoodSuppliers || []} // Ensure it's always an array
                onChange={handleWoodSuppliersChange}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    selected
                    // .map((status) => status.pickListDescription)
                    .join(", "),
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                  "& .MuiFormLabel-root": {
                    background:
                      "linear-gradient(to bottom, #EFEFEF 60%, white 40%)",
                  },
                }}
              >
                {assignedWoodSuppliers?.map((aws, index) => (
                  <MenuItem key={index} value={aws}>
                    <ListItemIcon>
                      <Checkbox
                      checked={selectedWoodSuppliers.some(
                        (item) => equalsIgnoreCase(item, aws)
                      )}
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                    </ListItemIcon>
                    <ListItemText primary={aws} />
                  </MenuItem>
                ))}
                <MenuItem value="all">
                  <ListItemIcon>
                    <Checkbox
                      checked={
                        selectedWoodSuppliers?.length ===
                        assignedWoodSuppliers?.length
                      }
                      indeterminate={
                        selectedWoodSuppliers?.length > 0 &&
                        selectedWoodSuppliers?.length <
                        assignedWoodSuppliers?.length
                      }
                      icon={<img src={CheckboxUntick} alt="Unchecked" />}
                      checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("woodSupplierTask.all")} />
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              marginLeft: 0,
              paddingBottom: 2,
              paddingRight: 3,
              width: "100%",
              backgroundColor: "#EFEFEF",
            }}
          >
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t("woodSupplierTask.search")}
                variant="outlined"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)} // Add this line
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#00000099" }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: "#00000099",
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "46px",
                    borderRadius: "5px",
                    "& input": {
                      height: "46px",
                      padding: "0 14px",
                      boxSizing: "border-box",
                    },
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                      borderRadius: "5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4C4C4",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  display: "flex",
                  backgroundColor: "#FFFFFF",
                  color: "#131619",
                  fontFamily: "Montserrat",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  textTransform: "none",
                  padding: 0,
                  "&:hover": {
                    backgroundColor: "lightgray",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "lightgray",
                  },
                  boxShadow: "none",
                  height: "46px",
                  border: "1px solid #C4C4C4",
                }}
              >
                <RefreshIcon sx={{ color: "#5A646E !important" }} />
                {t("woodSupplierTask.refreshPage")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={1.25}>
              <Button
                fullWidth
                variant="contained"
                // onClick={handleReopenSubmit}
                onClick={() => handleAssign()}
                // disabled={selectedForReopen.size === 0}
                sx={{
                  backgroundColor: "#EF7D00",
                  color: "#F8F8F8",
                  "&:hover": {
                    backgroundColor: "#F28F26",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#F9C77A",
                    color: "white",
                    opacity: 0.5,
                  },
                  boxShadow: "none",
                  height: "46px",
                }}
              >
                {t("woodSupplierTask.assign")}
              </Button>
            </Grid>
            {/* Replace the Approve button Grid item with this conditional rendering */}
            {!isWoodMonitor && (
              <Grid item xs={12} sm={1.25}>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleApprove()}
                  sx={{
                    backgroundColor: "#0F2B63",
                    color: "#F8F8F8",
                    "&:hover": {
                      backgroundColor: "#4868A3",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#7A8CB3",
                      color: "white",
                      opacity: 0.5,
                    },
                    boxShadow: "none",
                    height: "46px",
                  }}
                >
                  {t("woodSupplierTask.approve")}
                </Button>
              </Grid>
            )}
          </Grid>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              <WoodSupplierTable
                data={wsData}
                setData={setWsData}
                cropYear={selectedCropYear}
                cropYearId={selectedCropYearUid}
                cropSeasonId={selectedCropStage}
                onAssignmentUpdate={handleAssignmentUpdate}
                onDirectUnassign={handleDirectUnassign}
                selectedStatuses={selectedStatuses}
                 onViewSurvey={handleViewSurvey} // Add this prop
              />
              {wsData.length > 0 && (
                <div
                  style={{
                    position: "fixed",
                    width: "96vw", // Full viewport width
                    bottom: "50px", // Stick to bottom of viewport
                    left: "0", // Start from left edge
                    zIndex: 1000,
                    padding: "0 2vw",
                    backgroundColor: "white", // Add background if needed
                  }}
                >
                  <CustomPagination
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={(v) => setItemsPerPage(v)}
                    totalItems={totalItems}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
          <ReopenModal
            open={permanentIDModalOpen}
            onClose={() => setPermanentIDModalOpen(false)}
            // onConfirm={handleReopenConfirm}
            loading={loading}
          />
          <ApproveModal
            open={approveModalOpen}
            onClose={() => setApproveModalOpen(false)}
            onConfirm={() => handleApproveConfirm()}
            loading={loading}
          />
          <ApproveAssignModal
            open={approveAssignModalOpen}
            isApproveModal={isApproveModal}
            onClose={() => setApproveAssignModalOpen(false)}
            onConfirm={() => handleInitialConfirm()} // Change this to use the new handler
          />
          <UserSelectionModal
            open={userSelectionModalOpen}
            onClose={() => setUserSelectionModalOpen(false)}
            onConfirm={(userId) => handleAssignConfirm(userId)}
            loading={loading}
            users={usersList} // List of users from the API
            woodSuppliers={wsData} // Pass the wood supplier list here
          />
          <ConfirmationDialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={() => handleConfirmationResponse2()}
          />
          <GeneratePermanentIDDialog
            open={generatePermanentIDDialogOpen}
            onClose={() => setGeneratePermanentIDDialogOpen(false)}
            onConfirm={() => {
              setGeneratePermanentIDDialogOpen(false);
              handleApproveConfirm();
            }}
          />
        </Box>
      )}

      {currentView === "view" && (
        <ViewSurvey
          surveyData={selectedWoodSupplier}
          onBack={handleBack}
          onShowSignature={(signatureData) => handleShowSignature(signatureData)}
          onRefreshData={() => fetchWoodSupplierTasks(currentPage, itemsPerPage)}
          isApprover={isWoodMonitor}
          selectedCropStage={cropStages.find(
            (stage) => stage.cropSeasonId === selectedCropStage
          )?.cropSeasonName || ""}
          selectedCropStageId={selectedCropStage}
          year={selectedCropYear}
          read_only_user={false}
        />
      )}

      {currentView === "signature" && (
        <SignaturePage
          data={signatureData}
          onBack={() => setCurrentView("view")}
        />
      )}
    </Layout>
  );
};

export default WoodSupplierTask;
